import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { ArrowLeft, CheckCircle2, X, RefreshCw, Library, HelpCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { RoadSign } from '../../components/RoadSign';

export default function AdminCommunity() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('questions');
  const [pendingQ, setPendingQ] = useState([]);
  const [pendingR, setPendingR] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, r] = await Promise.all([
        api.adminListPendingCommunityQuestions(),
        api.adminListPendingResources(),
      ]);
      setPendingQ(q.questions || []);
      setPendingR(r.resources || []);
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approveQ = async (id) => {
    if (!window.confirm('Valider et intégrer cette question à la base officielle ?')) return;
    setActing(id);
    try {
      const r = await api.adminApproveCommunityQuestion(id);
      toast.success(`Question intégrée (${r.integrated_question_id})`);
      load();
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setActing(null); }
  };
  const rejectQ = async (id) => {
    const reason = window.prompt('Motif du rejet ?', '');
    if (reason === null) return;
    setActing(id);
    try { await api.adminRejectCommunityQuestion(id, reason); toast.success('Rejeté'); load(); }
    catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setActing(null); }
  };
  const approveR = async (id) => {
    setActing(id);
    try { await api.adminApproveResource(id); toast.success('Ressource publiée'); load(); }
    catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setActing(null); }
  };
  const rejectR = async (id) => {
    const reason = window.prompt('Motif du rejet ?', '');
    if (reason === null) return;
    setActing(id);
    try { await api.adminRejectResource(id, reason); toast.success('Rejetée'); load(); }
    catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setActing(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-community-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Button onClick={() => navigate('/admin')} variant="ghost" size="sm" className="-ml-2 mb-1">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back-office
          </Button>
          <h1 className="text-3xl font-black">Modération communauté</h1>
          <p className="text-sm text-muted-foreground mt-1">Validez ou rejetez les contributions des enseignants.</p>
        </div>
        <Button onClick={load} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-1" /> Actualiser</Button>
      </div>

      <div className="flex gap-2 border-b border-border">
        <button onClick={() => setTab('questions')} data-testid="tab-questions" className={cn('px-4 py-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5', tab === 'questions' ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-transparent text-muted-foreground')}>
          <HelpCircle className="w-4 h-4" /> QCM à valider ({pendingQ.length})
        </button>
        <button onClick={() => setTab('resources')} data-testid="tab-resources" className={cn('px-4 py-2 text-sm font-bold border-b-2 transition-colors flex items-center gap-1.5', tab === 'resources' ? 'border-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'border-transparent text-muted-foreground')}>
          <Library className="w-4 h-4" /> Ressources à valider ({pendingR.length})
        </button>
      </div>

      {loading ? (
        <div className="mc-card p-10 text-center text-muted-foreground">Chargement…</div>
      ) : tab === 'questions' ? (
        pendingQ.length === 0 ? (
          <div className="mc-card p-10 text-center text-sm text-muted-foreground">Rien à modérer dans les QCM.</div>
        ) : (
          <div className="space-y-3">
            {pendingQ.map(q => (
              <div key={q.id} className="mc-card p-5" data-testid={`pending-q-${q.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  {q.sign_code && <RoadSign code={q.sign_code} size={64} />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">Thème {q.theme_code}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><User className="w-3 h-3" /> {q.author_name}</span>
                    </div>
                    <p className="text-sm font-semibold">{q.question}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-1 text-xs mb-3">
                  {q.choices.map((c, i) => {
                    const isRight = String.fromCharCode(65 + i) === q.correct_answer;
                    return (
                      <div key={i} className={cn('flex items-start gap-1.5 p-1.5 rounded', isRight && 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] font-bold')}>
                        <span className="font-black">{String.fromCharCode(65 + i)}.</span><span>{c}</span>
                      </div>
                    );
                  })}
                </div>
                {q.explanation && <p className="text-xs text-muted-foreground italic mb-2">{q.explanation}</p>}
                {q.source && <p className="text-xs text-muted-foreground mb-3">{q.source}</p>}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
                  <Button onClick={() => approveQ(q.id)} disabled={acting === q.id} className="mc-btn-primary h-9" size="sm" data-testid={`approve-q-${q.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Valider et intégrer
                  </Button>
                  <Button onClick={() => rejectQ(q.id)} disabled={acting === q.id} variant="outline" size="sm" className="h-9 text-[#b42318] border-[#fecdd3]" data-testid={`reject-q-${q.id}`}>
                    <X className="w-3.5 h-3.5 mr-1.5" /> Rejeter
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        pendingR.length === 0 ? (
          <div className="mc-card p-10 text-center text-sm text-muted-foreground">Rien à modérer dans les ressources.</div>
        ) : (
          <div className="space-y-3">
            {pendingR.map(r => (
              <div key={r.id} className="mc-card p-5" data-testid={`pending-r-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">{r.type}</span>
                  {r.theme_code && <span className="text-[10px] font-mono font-bold rounded-full bg-muted px-1.5">Thème {r.theme_code}</span>}
                  <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground"><User className="w-3 h-3" /> {r.author_name}</span>
                </div>
                <h3 className="font-black text-base">{r.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                {r.body_md && <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/30 rounded p-2 mt-2">{r.body_md}</pre>}
                {r.external_url && <a href={r.external_url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[hsl(var(--primary))] mt-2 inline-block">{r.external_url}</a>}
                <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-border">
                  <Button onClick={() => approveR(r.id)} disabled={acting === r.id} className="mc-btn-primary h-9" size="sm" data-testid={`approve-r-${r.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Publier
                  </Button>
                  <Button onClick={() => rejectR(r.id)} disabled={acting === r.id} variant="outline" size="sm" className="h-9 text-[#b42318] border-[#fecdd3]" data-testid={`reject-r-${r.id}`}>
                    <X className="w-3.5 h-3.5 mr-1.5" /> Rejeter
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
